/** Header-action builder for a project's Site Register tab: the day stepper
 *  (Today, previous day, the day pill, next day), Refresh and Export CSV. The
 *  day pill carries neither onClick nor subActions: UnifiedAppHeader
 *  intercepts its id and draws it with a calendar under it.
 *
 *  A phone's header gets Refresh and Export CSV only. Its title is centred
 *  between the two clusters, so a stepper beside it squeezed "Site Register"
 *  out of the bar altogether; the page draws the stepper at the top of the
 *  register there instead, as Notes keeps its search field. No JSX. */

import { CalendarDays, ChevronLeft, ChevronRight, Download, RefreshCw } from '@/shared/ui/icons';
import {
  SITE_REGISTER_EXPORT_EVENT,
  SITE_REGISTER_REFRESH_EVENT,
  requestSiteRegisterDay,
  shiftSiteRegisterDay,
  type SiteRegisterHeaderState,
} from '../../../features/projects/site-register/siteRegisterHeader';
import type { MobileHeaderAction } from './types';

export const SITE_REGISTER_DAY_PILL_ID = 'site-register-day';

export function buildSiteRegisterHeaderActions(ctx: {
  state: SiteRegisterHeaderState;
  isDesktopViewport: boolean;
  trailingActions: MobileHeaderAction[];
}): void {
  const { state, isDesktopViewport, trailingActions } = ctx;

  if (isDesktopViewport) pushDayStepper(state, trailingActions);
  trailingActions.push(
    {
      id: 'site-register-refresh',
      icon: RefreshCw,
      ariaLabel: state.refreshing
        ? 'Refreshing the register'
        : state.updatedAt ? `Refresh, updated ${state.updatedAt}` : 'Refresh',
      disabled: !state.canRefresh || state.refreshing,
      group: 'page',
      onClick: () => window.dispatchEvent(new CustomEvent(SITE_REGISTER_REFRESH_EVENT)),
    },
    {
      id: 'site-register-export',
      icon: Download,
      desktopLabel: 'Export CSV',
      ariaLabel: 'Export CSV',
      disabled: !state.canExport,
      group: 'output',
      onClick: () => window.dispatchEvent(new CustomEvent(SITE_REGISTER_EXPORT_EVENT)),
    },
  );
}

function pushDayStepper(state: SiteRegisterHeaderState, trailingActions: MobileHeaderAction[]): void {
  if (!state.isToday) {
    trailingActions.push({
      id: 'site-register-today',
      label: 'Today',
      ariaLabel: 'Go to today',
      group: 'view',
      onClick: () => requestSiteRegisterDay(null),
    });
  }
  trailingActions.push(
    {
      id: 'site-register-previous-day',
      icon: ChevronLeft,
      ariaLabel: 'Previous day',
      group: 'view',
      onClick: () => shiftSiteRegisterDay(-1),
    },
    {
      id: SITE_REGISTER_DAY_PILL_ID,
      icon: CalendarDays,
      label: state.dayLabel,
      ariaLabel: `Choose a day, showing ${state.dayLabel}`,
      group: 'view',
    },
    {
      id: 'site-register-next-day',
      icon: ChevronRight,
      ariaLabel: 'Next day',
      disabled: state.isToday,
      group: 'view',
      onClick: () => shiftSiteRegisterDay(1),
    },
  );
}
